import { fromBlob, fromUrl, Pool, type GeoTIFF, type GeoTIFFImage, type TypedArray } from "geotiff";
import type { Bbox2 } from "../geo/bbox";
import { readCrs, type CrsInfo, type GeoKeys } from "../geo/crs";
import type { RangeSource } from "./rangeSource";

/** One resolution level of the file: index 0 is full resolution, the rest are overviews. */
export interface RasterLevel {
  /** Image (IFD) index inside the TIFF. */
  index: number;
  width: number;
  height: number;
  tileWidth: number;
  tileHeight: number;
  /** Ground sample distance in CRS units per pixel, always positive. */
  resX: number;
  resY: number;
}

export interface RasterWindow {
  data: TypedArray;
  width: number;
  height: number;
  samples: number;
}

let sharedPool: Pool | null = null;

/**
 * geotiff.js decodes tiles on worker threads. One pool is shared by every raster so a
 * viewer with an orthophoto and a DSM open does not spawn two sets of workers.
 */
function decoderPool(): Pool {
  if (!sharedPool) {
    // Without `Worker` (Node, or a locked-down embedding) a zero-sized pool decodes on the
    // calling thread instead of failing to construct one.
    if (typeof Worker === "undefined") {
      sharedPool = new Pool(0);
      return sharedPool;
    }
    const size = Math.max(2, Math.min(6, navigator.hardwareConcurrency || 4));
    sharedPool = new Pool(
      size,
      () => new Worker(new URL("../workers/geotiffDecoder.worker.ts", import.meta.url), { type: "module" }),
    );
  }
  return sharedPool;
}

/**
 * A tiled, overview-bearing GeoTIFF exposed as a pyramid of readable levels.
 *
 * Nothing is downloaded up front: only the IFDs are read on open, and pixels are pulled
 * per tile per level over HTTP range requests. The reference orthophoto is a 1.3 GB
 * BigTIFF (23935 x 50692 px, 512 px tiles, seven overviews) and opens in a few requests.
 */
export class GeoRaster {
  readonly levels: RasterLevel[] = [];
  crs: CrsInfo = { name: "unknown CRS", projected: true, unitToMetre: 1 };
  bbox: Bbox2 = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  samples = 1;
  bitsPerSample = 8;
  /** True for `SampleFormat` 3 (IEEE float), which elevation models normally use. */
  isFloat = false;
  noData: number | null = null;
  /** True when the file carries an alpha channel that should be honoured. */
  hasAlpha = false;

  private tiff!: GeoTIFF;
  private readonly images = new Map<number, GeoTIFFImage>();

  private constructor(readonly source: RangeSource) {}

  static async open(source: RangeSource): Promise<GeoRaster> {
    const raster = new GeoRaster(source);
    await raster.init();
    return raster;
  }

  private async init(): Promise<void> {
    const target = await this.source.geotiffTarget();
    this.tiff =
      target.kind === "url"
        ? await fromUrl(target.url, {
            // Small enough to keep the first IFD read cheap, large enough that the
            // directory and geo keys usually arrive in a single request.
            headers: {},
            blockSize: 64 * 1024,
            cacheSize: 256,
          })
        : await fromBlob(target.blob);

    const count = await this.tiff.getImageCount();
    const first = await this.image(0);

    const geoKeys = first.getGeoKeys() as GeoKeys | null;
    this.crs = readCrs(geoKeys);

    const [minX, minY, maxX, maxY] = first.getBoundingBox();
    this.bbox = { minX, minY, maxX, maxY };

    this.samples = first.getSamplesPerPixel();
    this.bitsPerSample = Number(first.getBitsPerSample(0)) || 8;
    this.isFloat = Number(first.getSampleFormat(0)) === 3;
    this.noData = first.getGDALNoData();

    const extraSamples = first.fileDirectory?.ExtraSamples;
    this.hasAlpha = this.samples === 4 || this.samples === 2 || (Array.isArray(extraSamples) && extraSamples.length > 0);

    const width = first.getWidth();
    const height = first.getHeight();
    for (let index = 0; index < count; index++) {
      const image = await this.image(index);
      const w = image.getWidth();
      const h = image.getHeight();
      // Reduced-resolution IFDs carry no geo tags of their own, so their ground sample
      // distance is derived from the full-resolution extent.
      this.levels.push({
        index,
        width: w,
        height: h,
        tileWidth: image.getTileWidth() || w,
        tileHeight: image.getTileHeight() || Math.min(h, 512),
        resX: (maxX - minX) / w,
        resY: (maxY - minY) / h,
      });
      if (index === 0 && (w !== width || h !== height)) {
        throw new Error(`${this.source.label}: inconsistent image dimensions`);
      }
    }
    // Some writers interleave masks or thumbnails; ordering by pixel count keeps level 0
    // as the sharpest image and the rest as a monotonic pyramid.
    this.levels.sort((a, b) => b.width * b.height - a.width * a.height);
  }

  private async image(index: number): Promise<GeoTIFFImage> {
    let cached = this.images.get(index);
    if (!cached) {
      cached = await this.tiff.getImage(index);
      this.images.set(index, cached);
    }
    return cached;
  }

  get width(): number {
    return this.levels[0]?.width ?? 0;
  }
  get height(): number {
    return this.levels[0]?.height ?? 0;
  }
  /** Full-resolution ground sample distance in CRS units. */
  get resolution(): number {
    return this.levels[0]?.resX ?? 1;
  }

  /**
   * Coarsest level whose ground sample distance is still finer than `targetRes`.
   * Used to pick an overview matching how many screen pixels a tile covers.
   */
  levelForResolution(targetRes: number): number {
    let chosen = 0;
    for (let i = 0; i < this.levels.length; i++) {
      if (this.levels[i].resX <= targetRes) chosen = i;
      else break;
    }
    return chosen;
  }

  /** Number of pyramid levels available. */
  get levelCount(): number {
    return this.levels.length;
  }

  /** Maps a CRS coordinate to fractional pixel coordinates of the given level. */
  crsToPixel(level: number, x: number, y: number): { px: number; py: number } {
    const lvl = this.levels[level];
    return {
      px: ((x - this.bbox.minX) / (this.bbox.maxX - this.bbox.minX)) * lvl.width,
      py: ((this.bbox.maxY - y) / (this.bbox.maxY - this.bbox.minY)) * lvl.height,
    };
  }

  /** Maps level pixel coordinates back to CRS coordinates (pixel corner convention). */
  pixelToCrs(level: number, px: number, py: number): { x: number; y: number } {
    const lvl = this.levels[level];
    return {
      x: this.bbox.minX + (px / lvl.width) * (this.bbox.maxX - this.bbox.minX),
      y: this.bbox.maxY - (py / lvl.height) * (this.bbox.maxY - this.bbox.minY),
    };
  }

  /**
   * Reads an interleaved pixel window from one level.
   *
   * `outWidth`/`outHeight` request resampling by geotiff.js, which is how a heightmap of a
   * fixed grid size is produced from an arbitrary source window.
   */
  async readWindow(
    level: number,
    window: [number, number, number, number],
    options: {
      outWidth?: number;
      outHeight?: number;
      samples?: number[];
      signal?: AbortSignal;
      /**
       * When false the window may extend past the image edge; geotiff.js pads it with the
       * fill value. Texture reads need this so a tile straddling the border keeps an exact
       * pixel-to-UV mapping instead of being silently stretched.
       */
      clampWindow?: boolean;
    } = {},
  ): Promise<RasterWindow> {
    const lvl = this.levels[level];
    if (!lvl) throw new Error(`${this.source.label}: no raster level ${level}`);
    const image = await this.image(lvl.index);

    const clamp = options.clampWindow !== false;
    const clamped: [number, number, number, number] = clamp
      ? [
          Math.max(0, Math.min(lvl.width, Math.floor(window[0]))),
          Math.max(0, Math.min(lvl.height, Math.floor(window[1]))),
          Math.max(0, Math.min(lvl.width, Math.ceil(window[2]))),
          Math.max(0, Math.min(lvl.height, Math.ceil(window[3]))),
        ]
      : [Math.floor(window[0]), Math.floor(window[1]), Math.ceil(window[2]), Math.ceil(window[3])];
    if (clamped[2] <= clamped[0] || clamped[3] <= clamped[1]) {
      return { data: new Uint8Array(0), width: 0, height: 0, samples: 0 };
    }

    const sampleList = options.samples;
    const result = (await image.readRasters({
      window: clamped,
      interleave: true,
      pool: decoderPool(),
      ...(sampleList ? { samples: sampleList } : {}),
      ...(options.outWidth ? { width: options.outWidth } : {}),
      ...(options.outHeight ? { height: options.outHeight } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      // Anything outside the raster (edge tiles) reads as the nodata value when one is
      // declared, so gaps stay distinguishable from real zero elevations.
      ...(this.noData !== null ? { fillValue: this.noData } : clamp ? {} : { fillValue: 0 }),
    })) as TypedArray & { width: number; height: number };

    const sampleCount = sampleList?.length ?? this.samples;
    return {
      data: result,
      width: result.width,
      height: result.height,
      samples: sampleCount,
    };
  }

  dispose(): void {
    this.images.clear();
    this.source.dispose();
  }
}

/**
 * Tells an orthophoto apart from a surface/elevation model by band structure alone, so a
 * project folder's files don't have to follow any particular naming convention: an
 * orthophoto is RGB or RGBA, a height model carries one elevation value per pixel (plus,
 * occasionally, a mask band).
 */
export function classifyRaster(raster: GeoRaster): "dem" | "ortho" {
  return raster.samples <= 2 ? "dem" : "ortho";
}

/**
 * True when a sample means "no data".
 *
 * Surface models mark voids either with a sentinel (commonly -9999 or -32767) or with
 * float NaN — the reference DSM uses NaN, and `NaN === NaN` is false, so an equality test
 * against the declared nodata value alone silently misses every hole.
 */
export function isNoData(value: number, noData: number | null): boolean {
  if (!Number.isFinite(value)) return true;
  if (noData !== null && Number.isFinite(noData) && value === noData) return true;
  // Sentinels well below any real elevation, used even when no nodata tag is written.
  return value <= -9999;
}

/** Releases the shared decoder workers; called when the viewer is torn down. */
export function destroyDecoderPool(): void {
  sharedPool?.destroy();
  sharedPool = null;
}
